import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslations } from "../../contexts/Translation";
import { useOrganizations, useProfile } from "../../contexts/UserProvider";
import { useAuth } from "../../lib/auth";
import { FeedbachCard, Improvement, OrgDash, Person, Survey, TrendUp } from "../Icons";
import JourneyBand from "../Decor/JourneyBand";
import { FeedbackJourneyLogo } from "../Icons/Logo";
import Link from "../Link/Link";
import styles from "./Layout.module.css";

type NavItemProps = {
  href: string;
  text: string;
  icon: ReactNode;
  pathname: string;
  // Detail pages live under a singular path, like /survey/:id under Surveys.
  detailPath?: string;
};

// "/" is only active on the dashboard itself; the others also on their sub pages.
const isActive = (href: string, pathname: string, detailPath?: string) =>
  href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`) || (!!detailPath && pathname.startsWith(`${detailPath}/`));

const NavItem = ({ href, text, icon, pathname, detailPath }: NavItemProps) => {
  const active = isActive(href, pathname, detailPath);
  return (
    <li>
      <Link className={[styles.navItem, active && styles.navItemActive].filter(Boolean).join(" ")} href={href} aria-current={active ? "page" : undefined}>
        <span className={styles.navIcon}>{icon}</span>
        {text}
      </Link>
    </li>
  );
};

const subscriptionKey = (status: string, type: string) => {
  if (status !== "active") return "subscriptionInactive";
  return type === "trial" ? "subscriptionTrial" : "subscriptionActive";
};

export const Sidebar = () => {
  const t = useTranslations("menu");
  const { pathname } = useLocation();
  const { currentRole, currentOrganization, subscription } = useOrganizations();
  const profile = useProfile();
  const { user } = useAuth();
  const showShared = ["owner", "member", "supporter"].includes(currentRole);
  const iconSize = { width: "20px", height: "20px" };

  return (
    <aside className={styles.sidebar}>
      <Link className={styles.sidebarLogo} href='/'>
        <FeedbackJourneyLogo w={190} />
      </Link>
      <nav className={styles.sidebarNav} aria-label={t("navigation")}>
        <div className={styles.navHeading} id='sidebar-my-journey'>
          {t("myFeedbach")}
        </div>
        <ul className={styles.navList} aria-labelledby='sidebar-my-journey'>
          <NavItem href='/' text={t("dashboard")} icon={<TrendUp {...iconSize} />} pathname={pathname} />
          <NavItem href='/surveys' detailPath='/survey' text={t("surveys")} icon={<Survey {...iconSize} />} pathname={pathname} />
          <NavItem href='/improvements' detailPath='/improvement' text={t("goals")} icon={<Improvement {...iconSize} />} pathname={pathname} />
          <NavItem href='/cards' text={t("card")} icon={<FeedbachCard {...iconSize} />} pathname={pathname} />
        </ul>
        {showShared && (
          <>
            <div className={styles.navHeading} id='sidebar-shared-journey'>
              {t("sharedFeedbach")}
            </div>
            <ul className={styles.navList} aria-labelledby='sidebar-shared-journey'>
              <NavItem href='/shared' text={t("sharedSurveys")} icon={<Survey {...iconSize} />} pathname={pathname} />
              <NavItem href='/shared-improvements' text={t("sharedGoals")} icon={<Improvement {...iconSize} />} pathname={pathname} />
              <NavItem href='/shared-cards' text={t("sharedCards")} icon={<FeedbachCard {...iconSize} />} pathname={pathname} />
              <NavItem href='/organization' text={t("orgDash")} icon={<OrgDash {...iconSize} />} pathname={pathname} />
            </ul>
          </>
        )}
      </nav>
      <div className={styles.sidebarFooter}>
        {currentOrganization && (
          <p className={styles.subscription} data-testid='sidebar-subscription'>
            {t(subscriptionKey(subscription.status, subscription.type))}
          </p>
        )}
        <Link className={styles.profileLink} href='/settings?tab=profile'>
          <span className={styles.navIcon}>
            <Person {...iconSize} />
          </span>
          <span>
            <span className={styles.profileName}>{profile?.candidate || user?.name}</span>
            <span className={styles.profileMeta}>{t("settings")}</span>
          </span>
        </Link>
      </div>
      <div className={styles.sidebarBand} aria-hidden='true'>
        <JourneyBand curve='rise' tail={false} />
      </div>
    </aside>
  );
};
